import { useEffect, useState } from "react";
import { Download, Play } from "lucide-react";
import { api, errText } from "@/lib/api";
import { useCustomer } from "@/lib/customer";
import { fmtDate, fmtMoney, fmtNumber, STAGES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const DEFAULT_COLUMNS = ["poNumber", "poDate", "branchName", "itemCount", "lineValue", "owner", "stageLabel", "ldStatus", "ageing"];

export default function Reports() {
  const { customerId, onExpiryClock } = useCustomer();
  const [columns, setColumns] = useState([]);
  const [selected, setSelected] = useState(DEFAULT_COLUMNS);
  const [mode, setMode] = useState(localStorage.getItem("kanex.reportMode") || "po");
  const [meta, setMeta] = useState({ people: [], states: [], stages: STAGES });
  const [filters, setFilters] = useState({ owner: "", stage: "", ldColour: "", state: "", status: "", search: "", fromDate: "", toDate: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/columns").then(({ data }) => setColumns(data.columns)).catch(() => {});
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, [customerId]);

  // a column this customer's flow no longer has is dropped quietly
  useEffect(() => {
    if (!columns.length) return;
    const have = new Set(columns.map((c) => c.key));
    setSelected((s) => {
      const kept = s.filter((k) => have.has(k));
      return kept.length === s.length ? s : kept.length ? kept : DEFAULT_COLUMNS.filter((k) => have.has(k));
    });
  }, [columns]);

  const toggle = (key) =>
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const run = async () => {
    if (selected.length === 0) {
      toast.error("Pick at least one column");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/reports/run", { columns: selected, filters, mode });
      setResult(data);
      toast.success(`${data.count} ${mode === "po" ? "PO" : "line"}(s)`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    try {
      const response = await api.post("/reports/export", { columns: selected, filters, mode }, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "kanex-report.csv";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const typeOf = (key) => columns.find((c) => c.key === key)?.type;
  const labelOf = (key) => columns.find((c) => c.key === key)?.label || key;

  const render = (value, key) => {
    const type = typeOf(key);
    if (value === null || value === undefined || value === "") return "—";
    if (type === "date") return fmtDate(value);
    if (type === "money") return fmtMoney(value);
    if (type === "number") return fmtNumber(value);
    return String(value);
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Reports</h1>
        <p className="mt-1 text-sm text-muted-foreground">Pick the columns and the filters, run it, export the result.</p>
      </div>

      <div className="panel flex flex-wrap items-center gap-3 p-3" data-testid="report-mode-switch">
        <Label className="field-label mb-0">Read it</Label>
        {[["po", "PO-wise"], ["line", "Line-wise"]].map(([value, label]) => (
          <Button
            key={value}
            size="sm"
            variant={mode === value ? "default" : "outline"}
            onClick={() => {
              setMode(value);
              localStorage.setItem("kanex.reportMode", value);
              setResult(null);
            }}
            data-testid={`report-mode-${value}`}
          >
            {label}
          </Button>
        ))}
        <span className="text-xs text-muted-foreground">
          {mode === "po"
            ? "One row per PO, with the item count and the total value."
            : "One row per item, exactly as the lines are recorded."}
        </span>
      </div>

      <div className="panel p-3 sm:p-4">
        <h2 className="text-sm font-medium">Filters</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2">
            <Label className="field-label">Search</Label>
            <Input value={filters.search} onChange={(e) => set("search", e.target.value)} data-testid="report-search-input" />
          </div>
          {[
            ["owner", "Owner", [["", "Everyone"], ["Unassigned", "Unassigned"], ...meta.people.map((p) => [p, p])]],
            ["stage", "Stage", [["", "Any stage"], ...(meta.stages || STAGES).map((s) => [String(s.value), s.label])]],
            ["ldColour", onExpiryClock ? "Expiry" : "LD status", [["", "Any"], ["red", onExpiryClock ? "Past expiry" : "LD running"], ["amber", onExpiryClock ? "Near expiry" : "At risk"], ["green", "On time"], ["grey", "Closed"]]],
            ["state", "State", [["", "Any state"], ...meta.states.map((s) => [s, s])]],
            ["status", "Open or closed", [["", "All"], ["open", "Open"], ["closed", "Closed"]]],
          ].map(([key, label, options]) => (
            <div key={key}>
              <Label className="field-label">{label}</Label>
              <select
                value={filters[key]}
                onChange={(e) => set(key, e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                data-testid={`report-${key}-filter`}
              >
                {options.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
          ))}
          <div>
            <Label className="field-label">PO date from</Label>
            <Input type="date" value={filters.fromDate} onChange={(e) => set("fromDate", e.target.value)} className="font-mono" data-testid="report-from-filter" />
          </div>
          <div>
            <Label className="field-label">PO date to</Label>
            <Input type="date" value={filters.toDate} onChange={(e) => set("toDate", e.target.value)} className="font-mono" data-testid="report-to-filter" />
          </div>
        </div>
      </div>

      <div className="panel p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium">Columns</h2>
          <button className="text-xs text-muted-foreground hover:underline" onClick={() => setSelected(DEFAULT_COLUMNS)} data-testid="report-reset-columns-button">
            Reset to default
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {[...new Set(columns.map((c) => c.group || "Other"))].map((group) => (
            <div key={group} className="w-full">
              <p className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">{group}</p>
              <div className="flex flex-wrap gap-2">
                {columns
                  .filter((c) => (c.group || "Other") === group)
                  .map((c) => (
                    <button
                      key={c.key}
                      onClick={() => toggle(c.key)}
                      data-testid={`report-column-${c.key}`}
                      className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                        selected.includes(c.key)
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border hover:bg-secondary"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={run} disabled={busy} data-testid="report-run-button">
            <Play className="mr-2 h-4 w-4" />
            Run the report
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="report-export-button">
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {result && (
        <div className="panel overflow-x-auto" data-testid="report-result">
          <table className="w-full text-left">
            <thead className="border-b border-border bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                {result.columns.map((key) => (
                  <th key={key} className="cell font-medium">{labelOf(key)}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {result.rows.map((row) => (
                <tr key={row.id} data-testid={`report-row-${row.id}`}>
                  {result.columns.map((key) => (
                    <td key={key} className={`cell ${typeOf(key) ? "font-mono" : ""}`}>
                      {render(row[key], key)}
                    </td>
                  ))}
                </tr>
              ))}
              {result.rows.length === 0 && (
                <tr>
                  <td colSpan={result.columns.length} className="p-8 text-center text-sm text-muted-foreground">
                    No rows match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
